import {
  fetchDistribuidorasLandingPage,
  getPlataformaApiBase,
  sendLeadStatusStep,
  simularEconomia,
  type DistribuidoraOption,
} from "@/api/economySimulator";
import {
  buildAquisicaoPortalUrl,
  getUtmQueryFromLocation,
  resolvePortalAquisicaoBaseUrl,
  resolvePortalLoginUrl,
} from "@/config/plataforma";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { EconomySimulatorSlider } from "@/components/economy-simulator/EconomySimulatorSlider";
import { maskCpfOrCnpj, normalizeCellphone } from "@/lib/inputMasks";
import { isValidCpfCnpj } from "@/lib/validateDocument";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

const simuladorCtaClassName =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-thopen-pink to-thopen-yellow font-display font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200";

const SIMULE_PATH =
  "/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia";

function digitsOnly(v: string): string {
  return v.replace(/\D/g, "");
}

export function HomeEconomySimulator() {
  const [uiStep, setUiStep] = useState<"form" | "result">("form");
  const [loadingSimulacao, setLoadingSimulacao] = useState(false);
  const [distribuidoras, setDistribuidoras] = useState<DistribuidoraOption[]>(
    []
  );
  const [distribuidorasError, setDistribuidorasError] = useState<string | null>(
    null
  );
  const [distribuidoraSelecionada, setDistribuidoraSelecionada] = useState("");
  const [mediaGasto, setMediaGasto] = useState(0);
  const [telefone, setTelefone] = useState("");
  const [documento, setDocumento] = useState("");
  // Controla se o campo de CPF/CNPJ já perdeu o foco pelo menos uma vez —
  // evita mostrar "incorreto" enquanto o usuário ainda está digitando.
  const [documentoTouched, setDocumentoTouched] = useState(false);
  const [optComunicacao, setOptComunicacao] = useState(false);
  const [thumbActive, setThumbActive] = useState(false);

  const [valorEconomiaMensal, setValorEconomiaMensal] = useState(0);
  const [valorEconomiaAnual, setValorEconomiaAnual] = useState(0);

  const [modalPerfil, setModalPerfil] = useState(false);
  const [modalErroSim, setModalErroSim] = useState(false);
  const [modalSemOfertaDistribuidora, setModalSemOfertaDistribuidora] =
    useState(false);
  const [modalCalculoIncompleto, setModalCalculoIncompleto] = useState(false);
  const [modalClienteCadastrado, setModalClienteCadastrado] = useState(false);
  const [leadHash, setLeadHash] = useState<string | null>(null);
  const [redirectingAquisicao, setRedirectingAquisicao] = useState(false);

  const hasApi = Boolean(getPlataformaApiBase());

  const portalLoginUrl = resolvePortalLoginUrl();

  const docDigits = digitsOnly(documento);
  const isDocumentoValido = isValidCpfCnpj(docDigits);
  // Só mostra erro quando o usuário já saiu do campo (blur) e o documento
  // tem um tamanho "completo" (11 ou 14) mas é inválido — evita piscar
  // "incorreto" enquanto ele ainda está digitando.
  const documentoInvalido =
    documentoTouched &&
    (docDigits.length === 11 || docDigits.length === 14) &&
    !isDocumentoValido;

  const isFormValid =
    isDocumentoValido &&
    digitsOnly(telefone).length >= 10 &&
    Boolean(distribuidoraSelecionada) &&
    mediaGasto > 0;

  useEffect(() => {
    const ac = new AbortController();
    if (!hasApi) {
      setDistribuidorasError(
        "Defina VITE_PLATAFORMA_API_BASE para carregar distribuidoras e simular."
      );
      return () => ac.abort();
    }

    const load = () => {
      (async () => {
        try {
          const list = await fetchDistribuidorasLandingPage(ac.signal);
          setDistribuidoras(list);
          setDistribuidorasError(null);
        } catch (e) {
          if ((e as Error).name === "AbortError") return;
          setDistribuidorasError(
            "Não foi possível carregar as distribuidoras. Verifique a API e o CORS."
          );
          console.error(e);
        }
      })();
    };

    // Deferred to idle time: this request is slow on the backend and was showing up in
    // Lighthouse's critical-path/LCP analysis simply because it started immediately on mount,
    // competing with the hero image/CSS. The dropdown isn't needed until the user starts
    // filling the form, so a short delay here is imperceptible but keeps it off that path.
    const idleId: number = "requestIdleCallback" in window
      ? requestIdleCallback(load, { timeout: 1500 })
      : window.setTimeout(load, 200);

    return () => {
      ac.abort();
      if ("cancelIdleCallback" in window) {
        cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
  }, [hasApi]);

  const handleClickLeft = () => {
    const novoValor = Math.max(mediaGasto - 50, 0);
    setMediaGasto(novoValor);
    setThumbActive(true);
  };

  const handleClickRight = () => {
    const novoValor = Math.min(mediaGasto + 50, 2050);
    setMediaGasto(novoValor);
    setThumbActive(true);
  };

  const handleSimulateEconomy = useCallback(async () => {
    if (!isFormValid || !hasApi) return;

    setLoadingSimulacao(true);
    try {
      const doc = digitsOnly(documento);
      const isCnpj = doc.length === 14;
      const tokenFromQuery =
        new URLSearchParams(window.location.search).get("token") ?? "";

      const data = await simularEconomia({
        idDistribuidora: distribuidoraSelecionada,
        valorConsumo: mediaGasto,
        telefoneDigits: digitsOnly(telefone),
        cpfDigits: isCnpj ? null : doc,
        cnpjDigits: isCnpj ? doc : null,
        tokenFromUrl: tokenFromQuery || undefined,
      });

      const dist = distribuidoras.find(
        (item) => String(item.idDistribuidora) === distribuidoraSelecionada
      );
      if (dist?.situacao != null && dist.situacao !== "A") {
        setModalSemOfertaDistribuidora(true);
        return;
      }

      if (mediaGasto < 250) {
        setModalPerfil(true);
        return;
      }

      if (
        data.plano === 0 &&
        data.mensagens != null &&
        data.mensagens !== "N/A"
      ) {
        setModalCalculoIncompleto(true);
        return;
      }

      if (data.situacao === "A") {
        setModalClienteCadastrado(true);
        return;
      }

      const anual = data.meses12 ?? 0;
      const mensal = data.mes ?? 0;
      if (!mensal && !anual) {
        setModalErroSim(true);
        return;
      }

      setValorEconomiaAnual(anual);
      setValorEconomiaMensal(mensal);
      setLeadHash(data.hash ?? null);
      setUiStep("result");
    } catch {
      toast.error("Não foi possível concluir a simulação. Tente novamente.");
    } finally {
      setLoadingSimulacao(false);
    }
  }, [
    distribuidoraSelecionada,
    distribuidoras,
    documento,
    hasApi,
    isFormValid,
    mediaGasto,
    telefone,
  ]);

  const handleEconomizeJa = useCallback(async () => {
    const apiBase = getPlataformaApiBase();
    if (!leadHash || !apiBase) {
      toast.error(
        "Não foi possível abrir a contratação. Refaça a simulação ou use o simulador completo."
      );
      return;
    }
    setRedirectingAquisicao(true);
    try {
      await sendLeadStatusStep(leadHash, "2");
      await sendLeadStatusStep(leadHash, "3");
      const portalBase = resolvePortalAquisicaoBaseUrl();
      const url = buildAquisicaoPortalUrl(
        portalBase,
        leadHash,
        getUtmQueryFromLocation()
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Não foi possível abrir o portal. Tente novamente.");
    } finally {
      setRedirectingAquisicao(false);
    }
  }, [leadHash]);

  const resetForm = () => {
    setUiStep("form");
    setDistribuidoraSelecionada("");
    setMediaGasto(0);
    setTelefone("");
    setDocumento("");
    setOptComunicacao(false);
    setThumbActive(false);
    setLeadHash(null);
  };

  const buttonInactive =
    "p-1 rounded-full hover:opacity-80 transition-all duration-150 ease-linear w-[50px] h-[50px] flex items-center justify-center shrink-0 bg-gray-400";
  const buttonActive =
    "p-1 rounded-full hover:opacity-80 transition-all duration-150 ease-linear w-[50px] h-[50px] flex items-center justify-center shrink-0 bg-thopen-pink";

  const enableLeftButton = mediaGasto > 0;
  const enableRightButton = mediaGasto < 2050;

  const thumbColor = thumbActive || mediaGasto > 0 ? "bg-thopen-pink" : "bg-gray-400";

  return (
    <div className="w-full max-w-[500px] rounded-[1.25rem] bg-white p-6 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.12)] sm:p-8">
      {uiStep === "form" ? (
        <form
          id="fsimulacao-home"
          className="flex flex-col items-center justify-center gap-4"
          onSubmit={(e) => e.preventDefault()}
        >
          <h2 className="text-center font-display text-2xl font-bold text-neutral-900">
            Um benefício exclusivo para os clientes Thopen Energy.
          </h2>
          <p className="text-center text-base font-normal leading-snug text-neutral-600">
            Economize até 15% na sua conta de energia, sem investimentos ou
            obras.
          </p>

          {distribuidorasError && (
            <p className="w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-sm text-amber-900">
              {distribuidorasError}{" "}
              <Link
                to={SIMULE_PATH}
                className="font-semibold text-thopen-pink underline-offset-2 hover:underline"
              >
                Abrir simulador completo
              </Link>
            </p>
          )}

          <div className="w-full space-y-2">
            <Label htmlFor="input_cpf_cnpj_home" className="sr-only">
              CPF ou CNPJ
            </Label>
            <input
              id="input_cpf_cnpj_home"
              className={cn(
                "flex h-12 w-full rounded-2xl border border-gray-800 bg-transparent px-4 py-3 text-base text-gray-700 shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-thopen-pink/40 md:text-lg",
                documentoInvalido && "border-destructive focus:ring-destructive/40"
              )}
              placeholder="CPF ou CNPJ (titular conta de energia)"
              autoComplete="off"
              value={maskCpfOrCnpj(documento)}
              onChange={(e) => setDocumento(e.target.value)}
              onBlur={() => setDocumentoTouched(true)}
            />
            {documentoInvalido && (
              <p className="text-left text-sm text-destructive">
                Número de CPF / CNPJ incorreto
              </p>
            )}
          </div>

          <div className="w-full space-y-2">
            <Label htmlFor="input_telefone_home" className="sr-only">
              Celular
            </Label>
            <input
              id="input_telefone_home"
              className="flex h-12 w-full rounded-2xl border border-gray-800 bg-transparent px-4 py-3 text-base text-gray-700 shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-thopen-pink/40 md:text-lg"
              placeholder="Celular/Whatsapp (titular conta de energia)"
              type="tel"
              autoComplete="off"
              value={normalizeCellphone(telefone)}
              onChange={(e) => setTelefone(e.target.value)}
            />
          </div>

          <div className="w-full space-y-2">
            <Label htmlFor="select_distrib_home" className="sr-only">
              Distribuidora
            </Label>
            <Select
              value={distribuidoraSelecionada === "" ? undefined : distribuidoraSelecionada}
              onValueChange={setDistribuidoraSelecionada}
            >
              <SelectTrigger
                id="select_distrib_home"
                className="h-12 w-full rounded-2xl border-gray-800 text-sm md:text-base [&_span]:text-sm md:[&_span]:text-base"
              >
                <SelectValue placeholder="Escolha entre as distribuidoras" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {distribuidoras.map((d) => (
                    <SelectItem
                      key={d.idDistribuidora}
                      value={String(d.idDistribuidora)}
                    >
                      {d.nomeDistribuidora}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <p className="text-center text-lg font-semibold leading-[27px] text-neutral-800">
            Média de gasto da sua conta
          </p>
          <p className="text-center text-base font-normal leading-none text-neutral-600">
            Elegibilidade mínima de R$ 250,00
          </p>

          <div className="flex w-full items-center justify-center gap-3">
            <button
              type="button"
              className={cn(
                "disabled:opacity-50",
                enableLeftButton ? buttonActive : buttonInactive
              )}
              onClick={handleClickLeft}
              disabled={!enableLeftButton}
              aria-label="Diminuir valor médio"
            >
              <ArrowLeft size={28} className="text-white max-md:h-8 max-md:w-8" />
            </button>
            <EconomySimulatorSlider
              id="slider_valor_home"
              value={[mediaGasto]}
              onValueChange={(value) => {
                setMediaGasto(value[0]);
                setThumbActive(true);
              }}
              max={2050}
              min={0}
              step={50}
              thumbValue={mediaGasto}
              thumbActiveClassName={thumbColor}
              thumbInactiveClassName="bg-gray-400"
              endText="+ de R$ 2.000,00"
              className="mx-4 flex-1"
            />
            <button
              type="button"
              className={cn(
                "disabled:opacity-50",
                enableRightButton ? buttonActive : buttonInactive
              )}
              onClick={handleClickRight}
              disabled={!enableRightButton}
              aria-label="Aumentar valor médio"
            >
              <ArrowRight
                size={28}
                className="text-white max-md:h-8 max-md:w-8"
              />
            </button>
          </div>

          <div className="flex w-full items-center justify-between gap-2 px-5">
            <span className="text-center text-sm font-bold text-gray-500">
              R$ 0,00
            </span>
            <span className="text-center text-sm font-bold text-gray-500">
              R$ 2.000,00 ou +
            </span>
          </div>

          <div className="flex items-start space-x-2 self-stretch">
            <Checkbox
              id="optin_com_home"
              checked={optComunicacao}
              onCheckedChange={(c) => setOptComunicacao(c === true)}
              className="mt-1 h-5 w-5 shrink-0 rounded border-2 border-neutral-700 data-[state=checked]:border-thopen-pink data-[state=checked]:bg-white data-[state=checked]:text-thopen-pink"
            />
            <Label
              htmlFor="optin_com_home"
              className="text-left text-sm font-normal text-neutral-900"
            >
              Aceito receber comunicações da Thopen Energy e confirmo que li e
              estou de acordo com a{" "}
              <Link
                to="/politica-privacidade"
                className="font-medium text-thopen-pink underline-offset-2 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </Link>
              .
            </Label>
          </div>

          <Button
            type="button"
            size="lg"
            className={cn(
              "h-12 w-full disabled:opacity-50",
              simuladorCtaClassName
            )}
            disabled={
              !isFormValid ||
              loadingSimulacao ||
              !optComunicacao ||
              !hasApi ||
              Boolean(distribuidorasError && distribuidoras.length === 0)
            }
            onClick={handleSimulateEconomy}
          >
            {loadingSimulacao ? (
              <>
                <Loader2 className="h-[18px] w-[18px] animate-spin" />
                Calculando
              </>
            ) : (
              "Simule agora"
            )}
          </Button>
        </form>
      ) : (
        <div className="flex flex-col items-center justify-center gap-6">
          <div className="my-4 flex w-full flex-col items-center justify-center gap-7">
            <h2 className="mb-2 text-center font-display text-2xl font-bold leading-tight text-neutral-900">
              Você pode ter
            </h2>
            <div className="flex w-full flex-col items-center justify-center space-y-2">
              <p className="text-3xl font-bold text-gray-900 md:text-4xl">
                {valorEconomiaAnual.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </p>
              <p className="text-neutral-600">Economia estimada anual</p>
            </div>
            <div className="flex w-full flex-col items-center justify-center space-y-2">
              <p className="text-3xl font-bold text-gray-900 md:text-4xl">
                {valorEconomiaMensal.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </p>
              <p className="text-neutral-600">Economia estimada mensal</p>
            </div>
          </div>
          <div className="my-4 h-px w-full border-b border-gray-200" />
          <Button
            type="button"
            size="lg"
            className={cn("h-12 w-full max-w-[332px]", simuladorCtaClassName)}
            disabled={redirectingAquisicao}
            onClick={handleEconomizeJa}
          >
            {redirectingAquisicao ? (
              <>
                <Loader2 className="h-[18px] w-[18px] animate-spin" />
                Abrindo portal…
              </>
            ) : (
              "Economize já"
            )}
          </Button>
          <button
            type="button"
            className="text-sm font-medium text-thopen-pink underline underline-offset-4 hover:opacity-80"
            onClick={resetForm}
          >
            Nova simulação
          </button>
        </div>
      )}

      <Dialog open={modalPerfil} onOpenChange={setModalPerfil}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ainda não temos uma oferta para você</DialogTitle>
            <DialogDescription>
              O benefício não está disponível para o perfil mensal de consumo
              abaixo de R$ 250,00. Ajuste o valor ou entre em contato quando sua
              média for compatível.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() => setModalPerfil(false)}
            >
              Entendi
            </Button>
            <Button
              className={cn(simuladorCtaClassName, "px-6")}
              asChild
            >
              <Link to={SIMULE_PATH}>Falar com especialista</Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={modalErroSim} onOpenChange={setModalErroSim}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Não foi possível concluir</DialogTitle>
            <DialogDescription>
              Não obtivemos valores de economia para esta combinação. Tente
              outra distribuidora ou fale com nosso time.
            </DialogDescription>
          </DialogHeader>
          <Button className="rounded-lg" onClick={() => setModalErroSim(false)}>
            Fechar
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={modalSemOfertaDistribuidora}
        onOpenChange={setModalSemOfertaDistribuidora}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Não há oferta disponível no momento</DialogTitle>
            <DialogDescription>
              Não se preocupe, entraremos em contato caso surja uma oportunidade
              para a sua distribuidora.
            </DialogDescription>
          </DialogHeader>
          <Button
            variant="outline"
            className="rounded-lg"
            onClick={() => setModalSemOfertaDistribuidora(false)}
          >
            Entendi
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={modalCalculoIncompleto}
        onOpenChange={setModalCalculoIncompleto}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Não foi possível realizar a sua simulação</DialogTitle>
            <DialogDescription>
              Tente acessar mais tarde ou refaça os dados informados.
            </DialogDescription>
          </DialogHeader>
          <Button className="rounded-lg" onClick={() => setModalCalculoIncompleto(false)}>
            Entendi
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={modalClienteCadastrado}
        onOpenChange={setModalClienteCadastrado}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Você já está cadastrado</DialogTitle>
            <DialogDescription>
              Acesse a área do cliente Thopen Energy para continuar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() => setModalClienteCadastrado(false)}
            >
              Fechar
            </Button>
            {portalLoginUrl ? (
              <Button
                className={cn(simuladorCtaClassName, "px-6")}
                asChild
              >
                <a
                  href={portalLoginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ir para o portal
                </a>
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground sm:self-center">
                URL de login do portal não configurada no ambiente.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}